/**
 * Request Deduplication Utility
 *
 * Prevents duplicate concurrent requests by caching in-flight promises.
 * This is especially useful for auth token fetching where multiple
 * concurrent API calls might trigger multiple auth checks simultaneously.
 */

interface CacheEntry<T> {
  promise: Promise<T>;
  timestamp: number;
}

class RequestCache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 30000; // 30 seconds

  /**
   * Wraps a function to ensure only one execution happens at a time for the same key.
   * If multiple calls come in with the same key, they'll all get the same promise.
   *
   * @param key - Unique identifier for the request
   * @param fn - Function to execute
   * @param ttl - Time to live in milliseconds (default: 30000ms)
   * @returns The result of the function
   */
  async deduplicate<T>(
    key: string,
    fn: () => Promise<T>,
    ttl: number = this.DEFAULT_TTL
  ): Promise<T> {
    const now = Date.now();
    const cached = this.cache.get(key);

    // Return cached promise if it exists and hasn't expired
    if (cached && now - cached.timestamp < ttl) {
      return cached.promise;
    }

    // Create new promise and cache it
    const promise = fn()
      .then((result) => {
        // Keep the result cached for a short time after completion
        // This helps with rapid successive calls
        return result;
      })
      .catch((error) => {
        // Remove from cache on error so it can be retried
        this.cache.delete(key);
        throw error;
      })
      .finally(() => {
        // Clean up after TTL expires
        setTimeout(() => {
          const entry = this.cache.get(key);
          if (entry && entry.timestamp === now) {
            this.cache.delete(key);
          }
        }, ttl);
      });

    this.cache.set(key, { promise, timestamp: now });
    return promise;
  }

  /**
   * Manually clear a specific cache entry
   */
  clear(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clearAll(): void {
    this.cache.clear();
  }
}

// Export a singleton instance
export const requestCache = new RequestCache();
